import React, {useCallback, useContext, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import capitalize from 'lodash.capitalize';
import {DateRangeInput, NumberInput, NumberInputTypes} from 'src/components/input';
import {RequiredMark} from 'src/components/required-mark/required-mark';
import {useDateValidation} from 'src/hooks/useDateValidation';
import {MAX_CONTRACT_FIELD_VALUE} from 'src/modules/profile/account/constants';
import {PackageSize} from 'src/openapi/admin/models/PackageSize';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {Float, Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {LocaleContext} from '@pb/kit-context-locale';


import {INPUT_WIDTH, NAMES} from '../constants';
import {getPricingBusy, packagesDataSelector} from '../services/selectors';


import type {EditSubscriptionContract} from '../typedef';
import type {ParsePackages} from './utils/parsePackages';

type Props = {
    onEditData: (data: EditSubscriptionContract) => void,
    onHasInvalidRange?: (value: boolean) => void,
};

type SubscriptionData = {
    packageSize: PackageSize,
    costPerCredit?: number,
    overageCost?: number,
    initialPackage?: number,
};
const DEFAULT_STATE: SubscriptionData = {
    packageSize: PackageSize.CUSTOM,
};

export const SubscriptionContractDetails = ({onEditData, onHasInvalidRange}: Props) => {
    const {locale} = useContext(LocaleContext);
    const [data, setData] = useState<SubscriptionData>(DEFAULT_STATE);
    const {packageSize, initialPackage, overageCost, costPerCredit} = data;
    const disabled = useSelector(getPricingBusy);

    const {packagesAcc, packageSizesList}: ParsePackages = useSelector(packagesDataSelector);
    const [endDate, setEndDate] = useState('');
    const [startDate, setStartDate] = useState('');

    const {hasInvalidRange} = useDateValidation(startDate, endDate, setStartDate);

    useEffect(() => {
        onHasInvalidRange?.(hasInvalidRange);
    }, [hasInvalidRange, onHasInvalidRange]);

    useEffect(() => {
        onEditData({
            packageSize,
            endDate,
            startDate,
            overageCost: overageCost || 0,
            costPerCredit: costPerCredit || 0,
            initialPackage: initialPackage || 0,
        });
    }, [packageSize, startDate, initialPackage, overageCost, endDate, costPerCredit, onEditData]);

    const onChangePackageSize = useCallback((size: PackageSize) => {
        const fields = size === PackageSize.CUSTOM ? {...data, packageSize: size} : {
            packageSize: size,
            costPerCredit: packagesAcc[size].costPerCredit,
            overageCost: packagesAcc[size].overageCost,
            initialPackage: packagesAcc[size].initialPackage,
        };

        setData(fields);
    }, [packagesAcc, data]);

    const onChangeNumber = useCallback((value?: number, name?: string) => {
        if (name) {
            setData(prevState => ({
                ...prevState,
                [name]: value,
                packageSize: PackageSize.CUSTOM,
            }));
        }
    }, []);

    return (
        <Group direction="v">
            <Group alignItems="start">
                <Item grow={1}>
                    <Group direction="v" gap="xl">
                        <Item>
                            <Label>Package Size</Label>
                            <ComboBox
                                {...{
                                    disabled,
                                    width: INPUT_WIDTH,
                                    value: packageSize,
                                    onChange: onChangePackageSize,
                                }}>
                                {packageSizesList.filter(e => e).map(pack => (
                                    <ComboboxItem
                                        key={`${pack}`}
                                        {...{
                                            caption: capitalize(pack || ''),
                                            value: pack,
                                        }}
                                    />
                                ))}
                            </ComboBox>
                        </Item>
                        <Item>
                            <Label>Contract Term</Label>
                            <Group direction="v" gap="s">
                                <DateRangeInput
                                    id="subscription-start-date-picker"
                                    label={<>Starts</>}
                                    width={INPUT_WIDTH}
                                    clearable={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    selectRangeType="start"
                                    onChange={setStartDate}
                                />
                                <DateRangeInput
                                    id="subscription-end-date-picker"
                                    label={<>Ends&nbsp;&nbsp;</>}
                                    width={INPUT_WIDTH}
                                    clearable={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    selectRangeType="end"
                                    onChange={setEndDate}
                                />
                            </Group>
                        </Item>
                    </Group>
                </Item>
                <Group direction="v">
                    <NumberInput
                        inputType={NumberInputTypes.POSITIVE_FLOAT}
                        label={<RequiredMark label="Cost per Credit"/>}
                        maxValue={MAX_CONTRACT_FIELD_VALUE}
                        value={costPerCredit}
                        width={INPUT_WIDTH}
                        readOnly={disabled}
                        placeholder="$, USD"
                        displayedValue={costPerCredit ? Float.format(costPerCredit, locale) : ''}
                        onBlur={value => onChangeNumber(value, NAMES.costPerCredit)}
                    />
                    <NumberInput
                        inputType={NumberInputTypes.POSITIVE_FLOAT}
                        label={<RequiredMark label="Overage Cost"/>}
                        maxValue={MAX_CONTRACT_FIELD_VALUE}
                        value={overageCost}
                        width={INPUT_WIDTH}
                        readOnly={disabled}
                        placeholder="$, USD"
                        displayedValue={overageCost ? Float.format(overageCost, locale) : ''}
                        onBlur={value => onChangeNumber(value, NAMES.overageCost)}
                    />
                    <NumberInput
                        label={<RequiredMark label="Initial Package"/>}
                        maxValue={MAX_CONTRACT_FIELD_VALUE}
                        width={INPUT_WIDTH}
                        value={initialPackage}
                        readOnly={disabled}
                        placeholder="Number of Credits"
                        displayedValue={initialPackage ? Integer.format(initialPackage, locale) : ''}
                        onBlur={value => onChangeNumber(value, NAMES.initialPackage)}
                    />
                </Group>
            </Group>
        </Group>
    );
};
